/**
 * The venue map pin, as plain data.
 *
 * The map component and the coordinate inputs both edit the same pin, so the
 * parsing, rounding and address lookup live here where a unit test can reach
 * them without Leaflet or a DOM.
 */

export interface Pin {
  latitude: number;
  longitude: number;
}

/**
 * OpenStreetMap's own tile server. No API key, and the usage policy is met by
 * the attribution below plus the low traffic of an admin-only screen. If the
 * attendee side ever renders maps, move to a commercial tile provider: the
 * policy forbids heavy use of these tiles.
 * https://operations.osmfoundation.org/policies/tiles/
 */
export const OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';

/** Where the map opens when the venue has no pin yet: Germany, zoomed out. */
export const DEFAULT_CENTER: Pin = { latitude: 51.1657, longitude: 10.4515 };
export const DEFAULT_ZOOM = 5;
export const PIN_ZOOM = 16;

/**
 * Six decimals is about 11 cm. A pin dragged on a map comes back with fifteen,
 * which reads as false precision in the coordinate inputs and says nothing a
 * directions link can use.
 */
export function roundCoordinate(value: number): number {
  // "+ 0" turns a rounded -0 (from -0.0000001) into 0, which would otherwise
  // print as "0" but compare unequal to it.
  return Math.round(value * 1e6) / 1e6 + 0;
}

/** Longitude from a map that has been panned round the globe, back in range. */
export function wrapLongitude(value: number): number {
  const wrapped = ((((value + 180) % 360) + 360) % 360) - 180;
  return wrapped === -180 && value > 0 ? 180 : wrapped;
}

export function pinFromMap(latitude: number, longitude: number): Pin {
  return {
    latitude: roundCoordinate(latitude),
    longitude: roundCoordinate(wrapLongitude(longitude)),
  };
}

/**
 * A coordinate as a person types it. A German keyboard or a pasted spreadsheet
 * value gives "52,52", so a single comma with no dot is the decimal separator.
 * Anything else that is not a plain number comes back as NaN.
 */
export function parseCoordinate(text: string): number {
  const value = text.trim();
  if (!value) return Number.NaN;
  const normalised = /^[+-]?\d+,\d+$/.test(value)
    ? value.replace(",", ".")
    : value;
  // `\d+(?:\.\d*)?` rather than `\d+\.?\d*`: both accept the same text, but
  // the second backtracks quadratically on a long run of digits.
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalised)) return Number.NaN;
  return Number(normalised);
}

const NUMBER = String.raw`[+-]?\d+(?:[.,]\d+)?`;
const DOT_NUMBER = String.raw`[+-]?\d+\.\d+`;

/**
 * "52.52, 13.40" copied from a map app and pasted into the latitude input, as
 * its two halves. Returns null when the text is not clearly a pair.
 *
 * The comma does two jobs, so only these read as a pair: a semicolon between
 * the numbers, a comma followed by a space, or a bare comma between two
 * numbers written with dots. "52,52" on its own stays one number with a
 * decimal comma.
 */
export function splitCoordinatePair(text: string): [string, string] | null {
  const value = text.trim();
  const patterns = [
    new RegExp(`^(${NUMBER})\\s*;\\s*(${NUMBER})$`),
    new RegExp(`^(${NUMBER}),\\s+(${NUMBER})$`),
    new RegExp(`^(${DOT_NUMBER}),(${DOT_NUMBER})$`),
  ];
  for (const pattern of patterns) {
    const match = pattern.exec(value);
    if (match) return [match[1], match[2]];
  }
  return null;
}

/**
 * Both blank is "no pin"; both valid is a pin; anything else is a sentence.
 *
 * Half a pin is the case worth naming: a latitude with no longitude reads as a
 * half-finished edit, and silently dropping it would look like the save worked.
 * A whole pair in the latitude input with the longitude blank is read as the
 * pair, because that is what pasting a copied location produces.
 */
export function readPin(
  latitude: string,
  longitude: string,
): Pin | null | string {
  let lat = latitude.trim();
  let lng = longitude.trim();
  if (lat && !lng) {
    const pair = splitCoordinatePair(lat);
    if (pair) [lat, lng] = pair;
  }
  if (!lat && !lng) return null;
  if (!lat || !lng) return "Enter both a latitude and a longitude, or neither.";
  const latValue = parseCoordinate(lat);
  const lngValue = parseCoordinate(lng);
  if (!Number.isFinite(latValue) || latValue < -90 || latValue > 90)
    return "Latitude must be a number between -90 and 90.";
  if (!Number.isFinite(lngValue) || lngValue < -180 || lngValue > 180)
    return "Longitude must be a number between -180 and 180.";
  return { latitude: latValue, longitude: lngValue };
}

/** What reading a pasted map link or coordinates gave: a pin or a sentence. */
export type MapInputReading = { pin: Pin } | { problem: string };

export const SHORT_LINK_PROBLEM =
  "Short links cannot be read here. Open it, copy the long address from the browser bar, or paste the coordinates.";
export const EMPTY_PROBLEM = "Paste a map link or two coordinates first.";
export const TOO_LONG_PROBLEM =
  "That is too long to be a map link or coordinates.";
export const UNREADABLE_PROBLEM =
  "No location found in that. Paste two numbers such as 52.5200, 13.4050, or a link from Google Maps, Apple Maps or OpenStreetMap.";
export const NO_COORDINATES_IN_LINK_PROBLEM =
  "That link has no coordinates in it. Open the place, copy the long address from the browser bar, or paste the coordinates.";
export const SWAPPED_PROBLEM =
  "The first number is too large for a latitude. Latitude comes first and runs from -90 to 90, so the two numbers look swapped.";
export const OUT_OF_RANGE_PROBLEM =
  "Those numbers are not a place on Earth. Latitude runs from -90 to 90 and longitude from -180 to 180.";

/**
 * The longest text read at all. A map link is a few hundred characters and a
 * phone's Share text with a place name around it is under a thousand; past
 * this the input is not one, and every pattern below is cheap to run on what
 * is left.
 */
export const MAX_MAP_INPUT_LENGTH = 2000;

/**
 * Hosts whose links are redirects to the real map page. The coordinates are
 * behind the redirect, and a browser cannot follow it: those sites do not
 * allow a page on another origin to read the response.
 */
const SHORT_LINK_HOSTS = new Set([
  "goo.gl",
  "maps.app.goo.gl",
  "g.co",
  "share.google",
  "maps.apple",
  "apple.co",
]);

function isShortLink(url: URL): boolean {
  // A trailing dot ("maps.app.goo.gl.") is the same host.
  const host = url.hostname
    .toLowerCase()
    .replace(/\.+$/, "")
    .replace(/^www\./, "");
  if (SHORT_LINK_HOSTS.has(host)) return true;
  // OpenStreetMap's own links carry their position; only /go/<code> does not.
  if (
    (host === "osm.org" || host === "openstreetmap.org") &&
    url.pathname.startsWith("/go/")
  )
    return true;
  // maps.apple.com/p/<id> is Apple's share link; only its place pages carry
  // coordinates, and this one does not.
  return host === "maps.apple.com" && /^\/p\//.test(url.pathname);
}

const DECIMAL = String.raw`[+-]?\d+(?:[.,]\d+)?`;
const DOT_DECIMAL = String.raw`[+-]?\d+(?:\.\d+)?`;

/**
 * Brackets and quotes round the text, and a full stop, comma or semicolon at
 * its end, taken off with a loop: a regex for this backtracks quadratically on
 * a long run of white space.
 */
function shaveWrapping(text: string): string {
  const before = "(\"'[";
  const after = ")\"'].,;";
  let start = 0;
  let end = text.length;
  while (
    start < end &&
    (/\s/.test(text[start]) || before.includes(text[start]))
  )
    start += 1;
  while (
    end > start &&
    (/\s/.test(text[end - 1]) || after.includes(text[end - 1]))
  )
    end -= 1;
  return text.slice(start, end);
}

/**
 * Two coordinate numbers in text, as their two halves, or null. Stricter than
 * a regex search on purpose: leftover words mean this is not a bare pair.
 *
 * The separators: a semicolon, a comma and a space, a bare comma when one side
 * has a dot (so "52,13" stays one number, not a pair), or white space alone.
 * Either number may use a decimal comma. Brackets, quotes and a full stop or
 * comma at the very end are shaved off first.
 */
function splitPair(text: string): [string, string] | null {
  const value = shaveWrapping(text).replace(/^loc:/i, "");
  const patterns = [
    new RegExp(`^(${DECIMAL})\\s*;\\s*(${DECIMAL})$`),
    new RegExp(`^(${DECIMAL})\\s*,\\s+(${DECIMAL})$`),
    new RegExp(`^(${DOT_DECIMAL})\\s*,\\s*(${DOT_DECIMAL})$`),
    new RegExp(`^(${DECIMAL})\\s+(${DECIMAL})$`),
  ];
  for (const [index, pattern] of patterns.entries()) {
    const match = pattern.exec(value);
    if (!match) continue;
    // A bare comma between two whole numbers is a decimal comma, not a split.
    if (index === 2 && !match[1].includes(".") && !match[2].includes("."))
      continue;
    return [match[1], match[2]];
  }
  return null;
}

type Candidate = { latitude: number; longitude: number };

function candidateFromPair(text: string): Candidate | null {
  const pair = splitPair(text);
  if (!pair) return null;
  const latitude = parseCoordinate(pair[0]);
  const longitude = parseCoordinate(pair[1]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

const inLatitudeRange = (value: number) => value >= -90 && value <= 90;
const inLongitudeRange = (value: number) => value >= -180 && value <= 180;

/** The first candidate that is a real place, or the sentence for the first that is not. */
function firstPin(candidates: Candidate[]): MapInputReading | null {
  let problem: string | null = null;
  for (const { latitude, longitude } of candidates) {
    if (inLatitudeRange(latitude) && inLongitudeRange(longitude))
      return {
        pin: {
          latitude: roundCoordinate(latitude),
          longitude: roundCoordinate(longitude),
        },
      };
    problem ??=
      inLatitudeRange(longitude) && inLongitudeRange(latitude)
        ? SWAPPED_PROBLEM
        : OUT_OF_RANGE_PROBLEM;
  }
  return problem ? { problem } : null;
}

/**
 * Where a map link carries its coordinates, most exact first: the place pin
 * Google writes as `!3d<lat>!4d<lng>`, a marker, then a query value, then the
 * `@lat,lng` the map was centred on, then the centre in OpenStreetMap's hash.
 */
function candidatesFromUrl(url: URL): Candidate[] {
  const candidates: Candidate[] = [];
  const add = (candidate: Candidate | null) => {
    if (candidate) candidates.push(candidate);
  };
  const pairOf = (lat: string | undefined, lng: string | undefined) => {
    if (lat === undefined || lng === undefined) return null;
    const latitude = parseCoordinate(lat);
    const longitude = parseCoordinate(lng);
    return Number.isFinite(latitude) && Number.isFinite(longitude)
      ? { latitude, longitude }
      : null;
  };

  let path = url.pathname;
  try {
    path = decodeURIComponent(path);
  } catch {
    // A stray % in the path: the raw text still has its coordinates in it.
  }
  const hash = url.hash.replace(/^#/, "");
  const hashParams = new URLSearchParams(hash.includes("=") ? hash : "");

  // A link that names a destination is a route, and the destination is the
  // venue. Its other numbers (the start, the map view) are not, so it is read
  // from the destination alone: a name there gives no pin, and never the
  // centre of the map in its place.
  const destinations = ["destination", "daddr"].flatMap((key) =>
    url.searchParams.getAll(key),
  );
  if (destinations.some((value) => value.trim())) {
    for (const value of destinations) add(candidateFromPair(value));
    return candidates;
  }

  // A directions path names several places; the last one is where the route
  // ends. Google may put an account number in front ("/maps/u/0/dir/...").
  const segments = path.split("/").filter(Boolean);
  const directions = segments.indexOf("dir");
  const isDirections =
    directions > 0 &&
    segments[0] === "maps" &&
    segments.slice(1, directions).every((s) => s === "u" || /^\d+$/.test(s));

  // Google's place pin, in the data part of a place link. Google writes the
  // latitude first, and either order is read by its key.
  if (!isDirections) {
    const data = `${path}${url.search}${url.hash}`;
    const latFirst = new RegExp(`!3d(${DOT_DECIMAL})!4d(${DOT_DECIMAL})`).exec(
      data,
    );
    const lngFirst = new RegExp(`!4d(${DOT_DECIMAL})!3d(${DOT_DECIMAL})`).exec(
      data,
    );
    if (latFirst) add(pairOf(latFirst[1], latFirst[2]));
    else if (lngFirst) add(pairOf(lngFirst[2], lngFirst[1]));
  }

  // OpenStreetMap's marker, in the query or the hash.
  for (const params of [url.searchParams, hashParams])
    add(
      pairOf(params.get("mlat") ?? undefined, params.get("mlon") ?? undefined),
    );

  for (const key of ["ll", "coordinate", "q", "query", "center", "viewpoint"])
    for (const value of url.searchParams.getAll(key))
      add(candidateFromPair(value));

  // /maps/place/52.52,13.405 and /maps/search/52.52,13.405
  const places = isDirections
    ? segments
        .slice(directions + 1)
        .filter(
          (segment) =>
            !segment.startsWith("@") && !/^(?:data|am)=/.test(segment),
        )
        .slice(-1)
    : segments;
  for (const segment of places)
    add(candidateFromPair(segment.replace(/\+/g, " ")));

  const viewport = new RegExp(`@(${DOT_DECIMAL}),(${DOT_DECIMAL})`).exec(path);
  if (viewport && !isDirections) add(pairOf(viewport[1], viewport[2]));

  // OpenStreetMap: #map=<zoom>/<lat>/<lng>
  const osm = new RegExp(
    `(?:^|&)map=\\d+(?:\\.\\d+)?/(${DOT_DECIMAL})/(${DOT_DECIMAL})`,
  ).exec(hash);
  if (osm) add(pairOf(osm[1], osm[2]));

  return candidates;
}

const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/i;
const BARE_HOST = /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/?#]\S*)?$/i;

/**
 * A map link or two coordinates pasted by an organiser, as a pin, or one
 * sentence on why it is not.
 *
 * Coordinates: "52.52, 13.405", "52.52 13.405", "52,52; 13,405", with a
 * decimal point or a German decimal comma. Latitude first.
 *
 * Links: Google Maps (`@lat,lng`, `!3d..!4d..`, `?q=`, `?query=`, `?ll=`,
 * `/place/lat,lng`), Apple Maps (`ll=`, `coordinate=`) and OpenStreetMap
 * (`#map=zoom/lat/lng`, `mlat=&mlon=`). The text may hold more than the link,
 * as a phone's Share sheet pastes a place name above it. A short link is
 * named as such, since its coordinates sit behind a redirect no page can read.
 *
 * Nothing is looked up and nothing is guessed: out-of-range numbers are
 * refused, and swapped ones are pointed out rather than swapped.
 */
export function readMapInput(text: string): MapInputReading {
  // Before any pattern runs, so no input can make one of them slow.
  if (text.length > MAX_MAP_INPUT_LENGTH) return { problem: TOO_LONG_PROBLEM };
  // A pair copied from a web page may use the typographic minus.
  const value = text.replace(/\u2212/g, "-").trim();
  if (!value) return { problem: EMPTY_PROBLEM };

  const linkText =
    URL_IN_TEXT.exec(value)?.[0] ?? (BARE_HOST.test(value) ? value : null);
  if (linkText) {
    let url: URL;
    try {
      url = new URL(
        /^https?:\/\//i.test(linkText) ? linkText : `https://${linkText}`,
      );
    } catch {
      return { problem: UNREADABLE_PROBLEM };
    }
    if (isShortLink(url)) return { problem: SHORT_LINK_PROBLEM };
    return (
      firstPin(candidatesFromUrl(url)) ?? {
        problem: NO_COORDINATES_IN_LINK_PROBLEM,
      }
    );
  }

  const pair = candidateFromPair(value);
  return (pair && firstPin([pair])) ?? { problem: UNREADABLE_PROBLEM };
}

export interface AddressMatch extends Pin {
  label: string;
  /**
   * The match is a postcode, a town or a district, so the pin sits at the
   * middle of an area and says nothing about where the venue is.
   */
  area: boolean;
}

/** What one press of "Find on map" found, and the wording that found it. */
export interface AddressLookup {
  /** The text sent to Nominatim, which may be shorter than what was typed. */
  query: string;
  /** Up to MAX_MATCHES places, best first. */
  matches: AddressMatch[];
}

export const MAX_MATCHES = 5;
/** Each lookup waits at least a second, so a fourth try would feel stuck. */
export const MAX_QUERIES = 3;
export const LOOKUP_TIMEOUT_MS = 10_000;

// One floor, room or wing part. Nominatim has none of these, and it treats
// every word of the query as one it must match, so a single "3. OG" turns a
// findable address into no result at all.
//
// Words that also name streets ("Am Stock", "Im Saal", "Zum Steinernen Haus")
// are left out of the keyword-and-number form, and these parts are only
// removed as a whole segment or after the house number, never from inside
// the street name.
const FLOOR_UNIT = [
  // "3. OG", "2. Etage", "4. Stock", "3.OG"
  String.raw`\d+\s*\.?\s*(?:OG|UG|Etage|Stockwerk|Stock|Obergeschoss|Untergeschoss)`,
  // "1st floor"
  String.raw`\d+(?:st|nd|rd|th)\s+floor`,
  // "EG", "Erdgeschoss", "Hinterhaus" on their own
  String.raw`(?:EG|OG|UG|DG|Erdgeschoss|Dachgeschoss|Hochparterre|Hinterhaus|Vorderhaus|Seitenflügel|Hinterhof|ground\s+floor)`,
  // "Raum 301", "Room B2.14", "Etage 2", "Stiege 2", "Top 5"
  String.raw`(?:Raum|Räume|Room|Zimmer|Suite|Floor|Level|Etage|Aufgang|Eingang|Stiege|Top|Gebäude|Building|Tür|Apt\.?|Whg\.?)\s*[\p{L}\d]*\d[\p{L}\d./-]*`,
].join("|");
const SEPARATOR = String.raw`[\s/|·,–-]`;
const ONLY_FLOOR = new RegExp(
  `^(?:${SEPARATOR}*(?:${FLOOR_UNIT})(?=${SEPARATOR}|$))+${SEPARATOR}*$`,
  "iu",
);
// "c/o Holtzbrinck", "z. Hd. Frau Meier", "Attn: Events team": a person or a
// company to hand the post to, which OpenStreetMap does not know.
const CARE_OF = /^(?:c\/o|z\.\s?Hd\.?|zu Händen|attn\.?:?|Attention)\s/iu;
// "26", "26a", "12-14", "5/2"
const HOUSE_NUMBER = /^\d+[a-z]?(?:[-/]\d+[a-z]?)?$/iu;

/**
 * "Hauptstr." and "Karl-Marx-Str." spelled out. Nominatim reads both, but a
 * candidate list that says "Straße" is easier to compare with what was typed.
 * The stem must be three letters or more, so "Distr." stays as it is.
 */
function expandStreet(text: string): string {
  return text
    .replace(/(^|[\s-])(\p{L}{3,})str\.(?=\s|$)/gu, "$1$2straße")
    .replace(/(^|[\s-])Str\.(?=\s|$)/gu, "$1Straße");
}

/**
 * One comma-separated part of an address with what OpenStreetMap cannot
 * match taken out: notes in brackets, a part that is only a floor or a room,
 * and floor or room text after the house number ("Kurfürstendamm 21 OG").
 */
function cleanSegment(segment: string): string {
  const text = segment
    .replace(/\([^)]*\)|\[[^\]]*\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text || ONLY_FLOOR.test(text)) return "";
  const tokens = text.split(" ");
  const houseNumber = tokens.findIndex(
    (token, index) =>
      index > 0 &&
      HOUSE_NUMBER.test(token) &&
      /\p{L}/u.test(tokens.slice(0, index).join(" ")),
  );
  if (houseNumber > 0 && houseNumber < tokens.length - 1) {
    const tail = tokens.slice(houseNumber + 1).join(" ");
    if (ONLY_FLOOR.test(tail))
      return expandStreet(tokens.slice(0, houseNumber + 1).join(" "));
  }
  return expandStreet(text);
}

const HAS_DIGIT = /\d/;
const POSTCODE = /^(?:[A-Z]{1,2}-)?\d{4,5}\s+/u;
/** "10117 Berlin" or "10117": a part that locates an area, not a building. */
const POSTCODE_AND_TOWN = /^(?:[A-Z]{1,2}-)?\d{4,5}(?:\s+\D*)?$/u;

/**
 * The address typed into the venue form, as the queries worth sending, most
 * faithful first.
 *
 * Nominatim finds a clean street address well and gives up on anything else
 * in the text: a venue name in front of the street, a floor or a room after
 * it, a "c/o" line. People type all three into an address field, so the
 * lookup tries, in order:
 *
 * 1. the address with floor, room, bracketed notes and "c/o" lines removed;
 * 2. the same from the street part, which drops a venue name written in
 *    front ("Holtzbrinck Haus, Gänsheidestraße 26, Stuttgart"). Skipped when
 *    the only part with a number is the postcode, since "10117 Berlin" alone
 *    would pin the middle of the postcode area;
 * 3. that name with the town, for a hotel or a hall OpenStreetMap knows by
 *    name ("Hotel Adlon, Berlin").
 *
 * Duplicates are dropped, and never more than MAX_QUERIES come back.
 */
export function addressQueries(text: string): string[] {
  const segments = text
    .split(/[,;\n]+/)
    .map((segment) => segment.trim())
    .filter((segment) => segment && !CARE_OF.test(segment))
    .map(cleanSegment)
    .filter(Boolean);
  if (segments.length === 0) return [];

  const queries = [segments.join(", ")];
  const street = segments.findIndex(
    (segment) => HAS_DIGIT.test(segment) && !POSTCODE_AND_TOWN.test(segment),
  );
  if (street > 0) {
    queries.push(segments.slice(street).join(", "));
  }
  if (segments.length > 1 && !HAS_DIGIT.test(segments[0])) {
    const town = segments[segments.length - 1].replace(POSTCODE, "").trim();
    if (town && !HAS_DIGIT.test(town)) queries.push(`${segments[0]}, ${town}`);
  }
  return [...new Set(queries)].slice(0, MAX_QUERIES);
}

/** Nominatim `addresstype` values for a result that is an area. */
const AREA_TYPES = new Set([
  "postcode",
  "city",
  "town",
  "village",
  "hamlet",
  "municipality",
  "suburb",
  "city_district",
  "district",
  "borough",
  "quarter",
  "neighbourhood",
  "county",
  "state",
  "region",
  "country",
]);

/**
 * The places Nominatim returns, as pins with labels. Two results on the same
 * spot with the same label (a building and the shop inside it often are)
 * show as one.
 */
export function readMatches(results: unknown): AddressMatch[] {
  if (!Array.isArray(results)) return [];
  const matches: AddressMatch[] = [];
  const seen = new Set<string>();
  for (const result of results as {
    lat?: unknown;
    lon?: unknown;
    display_name?: unknown;
    addresstype?: unknown;
  }[]) {
    const latitude = Number(result?.lat);
    const longitude = Number(result?.lon);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    if (typeof result.display_name !== "string" || !result.display_name)
      continue;
    const match = {
      ...pinFromMap(latitude, longitude),
      label: result.display_name,
      area:
        typeof result.addresstype === "string" &&
        AREA_TYPES.has(result.addresstype),
    };
    const key = `${match.latitude},${match.longitude},${match.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    matches.push(match);
    if (matches.length === MAX_MATCHES) break;
  }
  return matches;
}

/**
 * One query to OpenStreetMap's Nominatim. No API key. Its usage policy allows
 * one request a second and no search-as-you-type, so this runs only when the
 * organiser presses the button, and only through the gate below.
 * https://operations.osmfoundation.org/policies/nominatim/
 *
 * Returns an empty list when nothing matched and throws when the service
 * could not be reached or took longer than `timeoutMs`, because those need
 * different sentences on screen.
 */
export async function findAddress(
  query: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = LOOKUP_TIMEOUT_MS,
): Promise<AddressMatch[]> {
  const text = query.trim();
  if (!text) return [];
  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("limit", String(MAX_MATCHES));
  url.searchParams.set("q", text);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(url.toString(), {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok)
      throw new Error(`Address lookup failed (${response.status}).`);
    return readMatches(await response.json());
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Nominatim's policy is one request a second from a client. The button is
 * disabled while a lookup runs, but Enter in the address input is a second
 * way in, so the limit lives here where both paths meet: one lookup in
 * flight, and at least `gapMs` between the end of one request and the start
 * of the next, including the shorter retries inside one lookup. A call made
 * while one is in flight is dropped and reports "busy".
 *
 * A retry runs only when the previous query found nothing. A failed request
 * ends the lookup, since a service that is down or refusing will not answer
 * a shorter question either.
 */
export function createLookupGate({
  gapMs = 1000,
  timeoutMs = LOOKUP_TIMEOUT_MS,
  now = () => Date.now(),
  wait = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms)),
}: {
  gapMs?: number;
  timeoutMs?: number;
  now?: () => number;
  wait?: (ms: number) => Promise<void>;
} = {}) {
  let inFlight = false;
  let finishedAt = Number.NEGATIVE_INFINITY;
  return {
    async lookUp(
      address: string,
      fetchImpl: typeof fetch = fetch,
    ): Promise<AddressLookup | null | "busy"> {
      if (inFlight) return "busy";
      inFlight = true;
      try {
        for (const query of addressQueries(address)) {
          const remaining = finishedAt + gapMs - now();
          if (remaining > 0) await wait(remaining);
          try {
            const matches = await findAddress(query, fetchImpl, timeoutMs);
            if (matches.length > 0) return { query, matches };
          } finally {
            finishedAt = now();
          }
        }
        return null;
      } finally {
        inFlight = false;
      }
    },
    /** For tests, which run lookups closer together than a second. */
    reset() {
      inFlight = false;
      finishedAt = Number.NEGATIVE_INFINITY;
    },
  };
}

/** One gate for the whole page, so reopening the editor does not reset it. */
export const nominatimGate = createLookupGate();
